import React, {FC} from 'react';
import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  getDaysLeftInTrial,
  getHasTrialExpired,
  getIsInManagedTrial,
  getIsInTrial,
} from '../../../../store/data/company/companySelectors';
import {getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {composeUpgradePlanMessageToAdminAction} from '../../../../store/panels/app/banners/trialEndedBannerActions';
import {AppPanelId} from '../../../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {TrialEndedBanner} from './trialEndedBanner';

/*
 * Props.
 */

interface TrialEndedBannerContainerProps {
  panelId: AppPanelId;
}

type TrialEndedBannerContainerComponentProps = ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Component.
 */

export const TrialEndedBannerContainerComponent: FC<TrialEndedBannerContainerComponentProps> = (props) => {
  const {
    isGlobalAdmin,
    goToBilling,
    composeUpgradePlanMessageToAdmin,
    shouldHideTrialEndedBanner,
    daysLeftInTrial,
  } = props;

  // Check whether the company is in a trial or managed trial with 3 or fewer days left, or whether their trial has expired
  // without having previously paid for Front (i.e. it's not a churn).
  if (shouldHideTrialEndedBanner) {
    return null;
  }

  // Refresh the banner state when the days left in trial changes.
  const bannerKey = `daysLeft:${daysLeftInTrial}`;

  return (
    <TrialEndedBanner
      key={bannerKey}
      isGlobalAdmin={isGlobalAdmin}
      composeUpgradePlanMessageToAdmin={composeUpgradePlanMessageToAdmin}
      goToBilling={goToBilling}
      daysLeftInTrial={daysLeftInTrial}
    />
  );
};

/*
 * Selectors.
 */

const getIsInTrialOrHasTrialExpired = createSelector(
  [getIsInTrial, getHasTrialExpired],
  (isInTrial, hasTrialExpired) => isInTrial || hasTrialExpired,
);

// Hide the banner if the company is not a trial or managed trial with fewer than 3 days left, or if their trial has not expired
const getShouldHideTrialEndedBanner = createSelector(
  [getIsInTrialOrHasTrialExpired, getIsInManagedTrial, getDaysLeftInTrial],
  (isInTrialOrHasTrialExpired, isInManagedTrial, daysLeftInTrial) =>
    (!isInTrialOrHasTrialExpired && !isInManagedTrial) || daysLeftInTrial > 3,
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  daysLeftInTrial: getDaysLeftInTrial,
  isGlobalAdmin: getIsUserGlobalAdmin,
  shouldHideTrialEndedBanner: getShouldHideTrialEndedBanner,
}));

const mapDispatchToProps = makeMapDispatchToProps<TrialEndedBannerContainerProps>()(({panelId}) => ({
  goToBilling: () => navigateToLinkAction('/settings/billing'),
  composeUpgradePlanMessageToAdmin: composeUpgradePlanMessageToAdminAction,
}));

export const TrialEndedBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TrialEndedBannerContainerComponent);
